import {
  useAsyncDebounce,
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table';
import { ControlledPaginationTableProperties } from './types';
import { useCallback, useEffect, useState } from 'react';
import {
  Controls,
  ControlsSearch,
} from 'modules/Benchmarks/BenchmarkList/BenchmarkListStyles';
import { Table } from '../Table/TableStyles';
import { Skeleton } from 'components/Skeleton';
import { useHistory } from 'react-router-dom';
import { Pagination } from 'components/Pagination';
import {
  DEFAULT_TABLE_NO_DATA_MESSAGE,
  DEFAULT_TABLE_SEARCH_NO_DATA_MESSAGE,
} from 'components/Table/contants';
import Search from 'components/Table/Search/Search';
import { DEFAULT_DEBOUNCE_TIMEOUT } from 'components/Table/Search/constants';
import { KEY as PageSizeSearchParamKey } from 'hooks/usePageSizeURLSearchParam/usePageSizeURLSearchParam';
import { KEY as PageSearchParamKey } from 'hooks/usePageURLSearchParam/usePageURLSearchParam';

export const ControlledPaginationTable = ({
  columns,
  data,
  isLoading,
  pageCount,
  globalSearchFiltersProperties,
  actions,
  noDataMessage,
  debug,
  selector,
  pageIndex: selectedPageIndex,
  pageSize: selectedPageSize,
}: ControlledPaginationTableProperties) => {
  const [filteredTableData, setFilteredTableData] = useState(data);

  const globalSearchTestId = globalSearchFiltersProperties?.testId;
  const globalSearchEnabled = globalSearchFiltersProperties?.enabled;
  const globalSearchPlaceholder = globalSearchFiltersProperties?.placeholder;

  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);

  const tableInstance = useTable(
    {
      columns,
      data: filteredTableData,
      initialState: {
        pageIndex: selectedPageIndex,
        pageSize: selectedPageSize,
      },
      autoResetGlobalFilter: false,
      pageCount,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    setPageSize,
    state: { pageSize, globalFilter, filters },
    setGlobalFilter,
    rows,
  } = tableInstance;

  useEffect(() => {
    setFilteredTableData(data.slice(0));
  }, [data, globalFilter]);

  useCallback(() => {
    const startRow: number = pageSize * selectedPageIndex;
    const endRow: number = startRow + pageSize;
    setFilteredTableData(data.slice(startRow, endRow));
  }, [pageSize, selectedPageIndex, globalFilter]);

  const UpdatePageSizeSearchParam = (pagesize: number) => {
    searchParams.set(PageSizeSearchParamKey, pagesize.toString());
    history.push({ search: searchParams.toString() });
  };

  const UpdatePageSearchParam = (pageIndex: number) => {
    searchParams.set(PageSearchParamKey, (pageIndex + 1).toString());
    history.push({ search: searchParams.toString() });
  };

  const paginationOptions = {
    setPageSize: (pageSize: number) => {
      setPageSize(pageSize);
      UpdatePageSizeSearchParam(pageSize);
      gotoPage(0);
      UpdatePageSearchParam(0);
    },
    gotoPage: (page: number) => {
      gotoPage(page);
      UpdatePageSearchParam(page);
    },
    pageOptions,
    selector,
    pageIndex: selectedPageIndex,
    pageSize,
    pageCount,
    controlledPagination: true,
    size: rows.length,
  };

  //TODO: Rip this out a separate component
  const NoResults = () => {
    let message: string = DEFAULT_TABLE_NO_DATA_MESSAGE;
    if (noDataMessage) {
      message = noDataMessage;
    }

    if (rows.length !== 0) return <></>;

    if (globalSearchEnabled && globalFilter?.length > 0) {
      message = DEFAULT_TABLE_SEARCH_NO_DATA_MESSAGE;
    }

    return (
      <tr>
        <td colSpan={100}>{message}</td>
      </tr>
    );
  };

  useEffect(() => {
    if (globalSearchEnabled && globalFilter?.length > 0) {
      searchParams.set('page', '1');
      history.push({ search: searchParams.toString() });
    }
  }, [globalFilter]);

  const handleSearch = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, DEFAULT_DEBOUNCE_TIMEOUT);

  return (
    <>
      <Controls>
        {globalSearchEnabled ? (
          <ControlsSearch>
            <Search
              placeholder={globalSearchPlaceholder}
              testId={globalSearchTestId}
              value={globalFilter}
              onChange={(value) => {
                handleSearch(value);
              }}
            />
          </ControlsSearch>
        ) : null}
        {actions}
      </Controls>
      <Table>
        {/** TODO: I'd like to rip this out and use below code to replace the current Table Component.*/}
        <div style={{overflow:"auto"}}>
          <table {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any, index: any) => (
                    <th
                      {...column.getHeaderProps([
                        { className: column.className },
                      ])}
                    >
                      {column.render('Header')}
                      {column.hasFilter && (
                        <div data-testid={column.testid}>
                          {column.render('Filter')}
                        </div>
                      )}
                      {column.SortedBy ? (
                        <div data-testid={column.testid}>
                          {column.render('SortedBy')}
                        </div>
                      ) : null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {!isLoading ? (
                page?.map((row, i) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <Skeleton rows={3} columns={columns.length} />
              )}
              {!isLoading && <NoResults />}
            </tbody>
          </table>
        </div>
        {!isLoading && pageOptions.length > 0 ? (
          <Pagination {...paginationOptions} />
        ) : null}
        <br />
        {debug ? (
          <>
            <br />
            <div>
              {JSON.stringify(
                {
                  selectedPageIndex,
                  pageSize,
                  pageCount,
                  canNextPage,
                  canPreviousPage,
                },
                null,
                2
              )}
              <pre>
                <span>Current Page Index:&nbsp;</span>
                <code>{JSON.stringify(selectedPageIndex, null, 2)}</code>
              </pre>
              <pre>
                <span>Filter:&nbsp;</span>
                <code>{JSON.stringify(globalFilter ?? '', null, 2)}</code>
              </pre>
              <pre>
                <span>Column Filters:&nbsp;</span>
                <code>{JSON.stringify(filters ?? [], null, 2)}</code>
              </pre>
            </div>
          </>
        ) : null}
      </Table>
    </>
  );
};

export default ControlledPaginationTable;
